/**
 * DocImportModal — upload a document or photo (spreadsheet, planting list, field
 * notes, or a receipt) and let AI transcribe it into reviewable records (feature
 * #4). Two modes share this form: "Import records" (plants/blocks/events) and
 * "Scan receipt" — each separately gated + quota'd on the backend. Review-before-
 * save: candidates are committed via the normal create endpoints. Images are
 * downscaled on a canvas to fit the 2MB API limit; text/CSV are sent as-is.
 */
import { useMemo, useState } from 'react'
import { apiError } from '../../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, FileUp, Sprout, Grid3x3, ClipboardList, Receipt, Check } from 'lucide-react'
import { docImportApi, plantsApi, blocksApi, activityApi, expensesApi, EXPENSE_CATEGORIES, type CostCenter, type DocImportResult, type ParsedEvent } from '../../lib/api'

// Flatten the cost-center tree to {cc, depth}[] for an indented <select> (mirrors ExpensesPage).
function flattenCC(nodes: CostCenter[] | undefined, depth = 0, out: { cc: CostCenter; depth: number }[] = []) {
  for (const cc of nodes ?? []) { out.push({ cc, depth }); if (cc.children?.length) flattenCC(cc.children, depth + 1, out) }
  return out
}

const C = {
  card: 'var(--color-bg-card)', bg: 'var(--color-bg)', border: 'rgba(255,255,255,0.08)',
  amber: 'var(--color-primary)', green: '#2D6A2F', blue: '#5B8DEF', red: '#E5533C',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}

async function downscaleImage(file: File, maxEdge = 1400, quality = 0.82): Promise<Blob> {
  const dataUrl = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file) })
  const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl })
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale)
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
  return await new Promise<Blob>((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error('encode failed')), 'image/jpeg', quality))
}

const toISO = (d: string) => d ? (d.length === 10 ? `${d}T00:00:00Z` : d) : new Date().toISOString()

export function DocImportModal({ onClose, onImported }: { onClose: () => void; onImported?: () => void }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'entities' | 'receipt'>('entities')
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<DocImportResult | null>(null)
  const [done, setDone] = useState<Record<string, boolean>>({})
  const [ccId, setCcId] = useState('')

  const run = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first')
      const isImage = file.type.startsWith('image/')
      const payload: Blob = isImage ? await downscaleImage(file) : file
      const form = new FormData()
      form.append('file', payload, isImage ? 'doc.jpg' : file.name)
      form.append('mode', mode)
      return docImportApi.importDoc(form).then(r => r.data)
    },
    onSuccess: (d) => { setResult(d); setDone({}) },
  })

  const addPlants = useMutation({
    mutationFn: () => Promise.all((result?.plants ?? []).map(p => plantsApi.create({
      label: p.label || 'Imported plant', status: p.status || 'seedling',
      planted_at: p.planted_at || undefined,
      notes: [p.species && `species: ${p.species}`, p.variety && `variety: ${p.variety}`, p.notes].filter(Boolean).join(' · '),
    }))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['plants'] }); setDone(d => ({ ...d, plants: true })); onImported?.() },
  })
  const addBlocks = useMutation({
    mutationFn: () => Promise.all((result?.blocks ?? []).map(b => blocksApi.create({
      name: b.name || 'Imported block',
      tracking_mode: (b.tracking_mode === 'individual' ? 'individual' : 'aggregate'),
      plant_count: b.plant_count || 0,
    }))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['blocks'] }); setDone(d => ({ ...d, blocks: true })); onImported?.() },
  })
  const addEvents = useMutation({
    mutationFn: () => {
      const events: ParsedEvent[] = (result?.events ?? []).map(e => ({
        action_type: e.action_type || 'observation', occurred_at: toISO(e.occurred_at),
        description: e.description || '', quantity: e.quantity ?? null, unit: e.unit ?? '', confidence: 0.8,
      }))
      return activityApi.commit(events)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['activity'] }); setDone(d => ({ ...d, events: true })); onImported?.() },
  })

  // Receipt → expense ledger. Needs a cost center; reuse the shared cost-center
  // tree (same query key as ExpensesPage). The scanned receipt becomes one Expense.
  const ccQ = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => expensesApi.costCenters().then(res => res.data.cost_centers),
    enabled: result?.mode === 'receipt',
  })
  const flatCC = useMemo(() => flattenCC(ccQ.data), [ccQ.data])
  const selectedCC = ccId || flatCC[0]?.cc.id || ''
  const addReceipt = useMutation({
    mutationFn: () => {
      const rc = result?.receipt
      if (!rc) throw new Error('No receipt to add')
      if (!selectedCC) throw new Error('Add a cost center first')
      // AI category is free-form → snap to a known category (receipts default to purchase).
      const cat = (EXPENSE_CATEGORIES as readonly string[]).includes(rc.category || '') ? rc.category! : 'purchase'
      // Date: keep an ISO yyyy-mm-dd if the AI gave one, else today (backend Date is
      // time.Time — an empty string fails to unmarshal, so never send "").
      const d = rc.date && /^\d{4}-\d{2}-\d{2}/.test(rc.date) ? rc.date.slice(0, 10) : new Date().toISOString().slice(0, 10)
      const desc = (rc.line_items ?? []).map(li => li.description).filter(Boolean).slice(0, 6).join(', ') || rc.vendor || 'Receipt'
      return expensesApi.log({
        cost_center_id: selectedCC, amount: rc.total ?? 0, category: cat, date: d,
        vendor_name: rc.vendor || undefined, description: desc || undefined, currency: rc.currency || undefined,
      })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); setDone(dd => ({ ...dd, receipt: true })); onImported?.() },
  })

  const r = result
  const nP = r?.plants.length ?? 0, nB = r?.blocks.length ?? 0, nE = r?.events.length ?? 0

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 560, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileUp size={16} style={{ color: C.amber }} /> Automatic document import
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8 }}>
            {(['entities', 'receipt'] as const).map(m => (
              <button key={m} onClick={() => { setMode(m); setResult(null) }}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: mode === m ? 'color-mix(in srgb, var(--color-primary) 13%, transparent)' : C.bg, border: `1px solid ${mode === m ? 'color-mix(in srgb, var(--color-primary) 33%, transparent)' : C.border}`, color: mode === m ? C.amber : C.muted }}>
                {m === 'entities' ? 'Import records' : 'Scan receipt'}
              </button>
            ))}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: C.faint }}>
            {mode === 'entities'
              ? 'Upload a spreadsheet, planting list, field notes, or a photo of them — it automatically extracts plants, blocks, and events for review.'
              : 'Upload a photo or PDF of a receipt/invoice — it automatically extracts vendor, date, total, and line items.'}
          </p>

          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 90, border: `1.5px dashed ${C.border}`, borderRadius: 10, cursor: 'pointer', color: C.muted, background: C.bg }}>
            <FileUp size={22} />
            <span style={{ fontSize: 13 }}>{file ? file.name : 'Tap to choose a file or photo'}</span>
            <input type="file" accept="image/*,.csv,.txt,.pdf,application/pdf" style={{ display: 'none' }} onChange={e => { setFile(e.target.files?.[0] ?? null); setResult(null) }} />
          </label>

          <button disabled={!file || run.isPending} onClick={() => run.mutate()}
            style={{ padding: '10px 0', borderRadius: 8, background: C.amber, border: 'none', color: '#1a1300', fontSize: 14, fontWeight: 700, cursor: file && !run.isPending ? 'pointer' : 'not-allowed' }}>
            {run.isPending ? 'Reading…' : 'Extract'}
          </button>
          {run.isError && <div style={{ fontSize: 12, color: C.red }}>{apiError(run.error, 'Failed')}</div>}

          {r && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              {r.warning && <div style={{ fontSize: 12, color: C.amber, background: 'color-mix(in srgb, var(--color-primary) 10%, transparent)', padding: '8px 10px', borderRadius: 8 }}>{r.warning}</div>}

              {r.mode === 'entities' && r.ai_used && (
                <>
                  {nP + nB + nE === 0 && <div style={{ fontSize: 13, color: C.muted }}>No records found in this document.</div>}
                  {nP > 0 && <CommitGroup icon={<Sprout size={14} style={{ color: C.green }} />} title={`${nP} plant${nP > 1 ? 's' : ''}`}
                    rows={r.plants.map(p => `${p.label || '(no label)'}${p.variety ? ` — ${p.variety}` : ''}${p.planted_at ? ` (${p.planted_at})` : ''}`)}
                    done={!!done.plants} busy={addPlants.isPending} onAdd={() => addPlants.mutate()} />}
                  {nB > 0 && <CommitGroup icon={<Grid3x3 size={14} style={{ color: C.blue }} />} title={`${nB} block${nB > 1 ? 's' : ''}`}
                    rows={r.blocks.map(b => `${b.name || '(no name)'} — ${b.tracking_mode}${b.plant_count ? `, ${b.plant_count} plants` : ''}`)}
                    done={!!done.blocks} busy={addBlocks.isPending} onAdd={() => addBlocks.mutate()} />}
                  {nE > 0 && <CommitGroup icon={<ClipboardList size={14} style={{ color: C.amber }} />} title={`${nE} event${nE > 1 ? 's' : ''}`}
                    rows={r.events.map(e => `${e.action_type}${e.occurred_at ? ` ${e.occurred_at}` : ''} — ${e.description}`)}
                    done={!!done.events} busy={addEvents.isPending} onAdd={() => addEvents.mutate()} />}
                </>
              )}

              {r.mode === 'receipt' && r.receipt && (
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}><Receipt size={14} style={{ color: C.amber }} /> Receipt</div>
                  <Kv k="Vendor" v={r.receipt.vendor || '—'} />
                  <Kv k="Date" v={r.receipt.date || '—'} />
                  <Kv k="Total" v={r.receipt.total != null ? `${r.receipt.currency || '$'} ${r.receipt.total}` : '—'} />
                  <Kv k="Category" v={r.receipt.category || '—'} />
                  {r.receipt.line_items.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Line items</div>
                      {r.receipt.line_items.map((li, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.text, padding: '2px 0' }}>
                          <span>{li.description}</span><span style={{ color: C.muted }}>{li.amount != null ? `$${li.amount}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                    {done.receipt ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#4ADE80' }}><Check size={13} /> Added to your expense ledger</span>
                    ) : flatCC.length === 0 ? (
                      <div style={{ fontSize: 11, color: C.faint }}>{ccQ.isLoading ? 'Loading cost centers…' : 'Add a cost center on the Expenses page first, then this can post to your ledger.'}</div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <label style={{ display: 'block', fontSize: 11, color: C.muted, marginBottom: 3 }}>Cost center</label>
                          <select value={selectedCC} onChange={e => setCcId(e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 7, fontSize: 12, background: C.bg, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>
                            {flatCC.map(({ cc, depth }) => <option key={cc.id} value={cc.id}>{' '.repeat(depth * 2)}{cc.name}</option>)}
                          </select>
                        </div>
                        <button onClick={() => addReceipt.mutate()} disabled={addReceipt.isPending || r.receipt.total == null}
                          title={r.receipt.total == null ? 'No total was detected on this receipt' : 'Record this receipt as an expense'}
                          style={{ fontSize: 12, fontWeight: 600, padding: '7px 12px', borderRadius: 7, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber, cursor: addReceipt.isPending ? 'wait' : (r.receipt.total == null ? 'not-allowed' : 'pointer'), opacity: r.receipt.total == null ? 0.5 : 1 }}>
                          {addReceipt.isPending ? 'Adding…' : 'Add to expenses'}
                        </button>
                      </div>
                    )}
                    {addReceipt.isError && <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>{(addReceipt.error as Error)?.message || 'Could not add the expense.'}</div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function CommitGroup({ icon, title, rows, done, busy, onAdd }: { icon: React.ReactNode; title: string; rows: string[]; done: boolean; busy: boolean; onAdd: () => void }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: C.muted }}>{icon} {title}</div>
        {done
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4ADE80' }}><Check size={12} /> added</span>
          : <button onClick={onAdd} disabled={busy} style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 7, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.amber, cursor: busy ? 'wait' : 'pointer' }}>{busy ? 'Adding…' : 'Add all'}</button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.slice(0, 12).map((t, i) => <div key={i} style={{ fontSize: 12, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 9px' }}>{t}</div>)}
        {rows.length > 12 && <div style={{ fontSize: 11, color: C.faint }}>+{rows.length - 12} more…</div>}
      </div>
    </div>
  )
}
function Kv({ k, v }: { k: string; v: string }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}><span style={{ color: C.muted }}>{k}</span><span style={{ color: C.text, fontWeight: 600 }}>{v}</span></div>
}
